<template>
  <div class="min-h-screen bg-gradient-to-b from-[#142a4a] via-[#18365f] to-[#1b4475]">
    <div class="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-12">
      <div class="mb-8 mt-4">
        <h1 class="text-3xl font-bold text-slate-100">Provedor de envio de E-mail</h1>
        <p class="text-slate-400 mt-2 text-sm">
          Todos os e-mails da plataforma serão enviados com base nessa configuração.
        </p>
      </div>

      <div v-if="loadError" class="mb-4 p-4 bg-red-500/10 border border-red-400/40 text-red-300 rounded-lg text-sm">
        {{ loadError }}
      </div>

      <div v-if="loading" class="text-center py-12 text-slate-400">Carregando…</div>

      <div
        v-else
        class="rounded-xl border border-cyan-400/30 bg-slate-900/80 backdrop-blur-sm shadow-xl shadow-black/30 p-6"
      >
        <form class="space-y-6" @submit.prevent="openSaveConfirm">
          <div>
            <div v-if="formError" class="mb-4 bg-red-500/10 border border-red-400/40 text-red-300 px-4 py-3 rounded-lg text-sm">
              {{ formError }}
            </div>

            <div
              v-if="testResult?.ok"
              class="mb-4 bg-emerald-500/10 border border-emerald-400/40 text-emerald-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2"
            >
              <span>✓</span> {{ testResult.message || 'Validado!' }}
            </div>
            <div
              v-if="testError"
              class="mb-4 bg-red-500/10 border border-red-400/40 text-red-300 px-4 py-3 rounded-lg text-sm"
            >
              {{ testError }}
            </div>

            <div class="mb-6">
              <span id="delivery-type-label" class="block text-sm font-medium text-slate-300 mb-2">Tipo de envio</span>
              <div
                class="flex flex-wrap gap-3"
                role="radiogroup"
                aria-labelledby="delivery-type-label"
              >
                <label
                  :class="[
                    'inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm cursor-pointer transition',
                    form.delivery_type === 'smtp'
                      ? 'border-cyan-400/50 bg-cyan-400/10 text-slate-100 ring-1 ring-cyan-400/25'
                      : 'border-slate-500/80 bg-slate-800/55 text-slate-300 hover:border-slate-500'
                  ]"
                >
                  <input
                    v-model="form.delivery_type"
                    type="radio"
                    value="smtp"
                    class="h-4 w-4 border-slate-500 bg-slate-800 text-cyan-400 focus:ring-2 focus:ring-cyan-400/40 focus:ring-offset-0 focus:ring-offset-transparent"
                  />
                  <span>SMTP (servidor próprio)</span>
                </label>
                <label
                  :class="[
                    'inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm cursor-pointer transition',
                    form.delivery_type === 'api'
                      ? 'border-cyan-400/50 bg-cyan-400/10 text-slate-100 ring-1 ring-cyan-400/25'
                      : 'border-slate-500/80 bg-slate-800/55 text-slate-300 hover:border-slate-500'
                  ]"
                >
                  <input
                    v-model="form.delivery_type"
                    type="radio"
                    value="api"
                    class="h-4 w-4 border-slate-500 bg-slate-800 text-cyan-400 focus:ring-2 focus:ring-cyan-400/40 focus:ring-offset-0 focus:ring-offset-transparent"
                  />
                  <span>API (provedor)</span>
                </label>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-300 mb-1">E-mail para envio (From) <span class="text-red-400">*</span></label>
                <input
                  v-model="form.from_address"
                  type="email"
                  required
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-300 mb-1">Reply-To (opcional)</label>
                <input
                  v-model="form.reply_to"
                  type="text"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  placeholder="Nome &lt;email@dominio.com&gt;"
                />
                <p v-if="form.delivery_type === 'smtp' || form.delivery_type === 'api'" class="text-[11px] text-slate-500 mt-1">
                  Para <strong class="text-slate-400 font-medium">enviar e-mail de teste</strong> (SMTP ou API), preencha com o e-mail que deve receber a mensagem (o mesmo valor é usado no cabeçalho Reply-To).
                </p>
              </div>
            </div>

            <div v-if="form.delivery_type === 'api'">
              <div class="border-b border-slate-700/70 pb-3 mb-4">
                <h3 class="text-base font-semibold text-slate-200">Provedor (API)</h3>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-slate-300 mb-1">Provedor <span class="text-red-400">*</span></label>
                  <select
                    v-model="form.api_provider"
                    :required="form.delivery_type === 'api'"
                    class="w-full max-w-md px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 text-sm"
                  >
                    <option value="" disabled class="bg-slate-900">Selecione…</option>
                    <option value="sendgrid" class="bg-slate-900">SendGrid</option>
                    <option value="mailgun" class="bg-slate-900">Mailgun</option>
                    <option value="resend" class="bg-slate-900">Resend</option>
                    <option value="postmark" class="bg-slate-900">Postmark</option>
                    <option value="amazon_ses" class="bg-slate-900">Amazon SES</option>
                    <option value="custom" class="bg-slate-900">Personalizado (URL própria)</option>
                  </select>
                </div>

                <div v-if="form.api_provider === 'amazon_ses'" class="md:col-span-2">
                  <label class="block text-sm font-medium text-slate-300 mb-1">Região AWS</label>
                  <input
                    v-model="form.api_region"
                    type="text"
                    class="w-full max-w-md px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                    placeholder="ex.: us-east-1"
                  />
                </div>

                <div v-if="form.api_provider === 'custom'" class="md:col-span-2">
                  <label class="block text-sm font-medium text-slate-300 mb-1">URL base da API</label>
                  <input
                    v-model="form.api_endpoint"
                    type="url"
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                    placeholder="https://api.exemplo.com/v1"
                  />
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-slate-300 mb-1">Chave de API <span class="text-red-400">*</span></label>
                  <div class="relative max-w-xl">
                    <input
                      v-model="form.api_key"
                      :type="showApiKey ? 'text' : 'password'"
                      autocomplete="new-password"
                      class="w-full pl-3 pr-11 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                      :placeholder="editingId && form.api_key_configured ? 'Deixe em branco para manter' : ''"
                    />
                    <button
                      type="button"
                      class="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-400 hover:text-[#2AD7DE] focus:outline-none focus-visible:text-[#2AD7DE] rounded-r-lg"
                      :aria-label="showApiKey ? 'Ocultar chave de API' : 'Mostrar chave de API'"
                      :aria-pressed="showApiKey"
                      @click="showApiKey = !showApiKey"
                    >
                      <svg v-if="!showApiKey" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    </button>
                  </div>
                  <label class="mt-2 flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <input v-model="form.clear_api_key" type="checkbox" class="rounded border-slate-500" />
                    Remover chave salva
                  </label>
                  <p v-if="form.api_provider === 'amazon_ses'" class="text-[11px] text-slate-500 mt-2">
                    Amazon SES: use <strong class="text-slate-400 font-medium">Access key ID:Secret access key</strong> num único campo (dois valores separados por <code class="text-slate-400">:</code>), com permissões de envio na região indicada.
                  </p>
                </div>
              </div>
            </div>

            <div v-if="form.delivery_type === 'smtp'">
              <div class="border-b border-slate-700/70 pb-3 mb-4">
                <h3 class="text-base font-semibold text-slate-200">Servidor SMTP</h3>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-1">Servidor SMTP <span class="text-red-400">*</span></label>
                  <input
                    v-model="form.smtp_address"
                    type="text"
                    :required="form.delivery_type === 'smtp'"
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-1">Porta <span class="text-red-400">*</span></label>
                  <input
                    v-model.number="form.smtp_port"
                    type="number"
                    min="1"
                    max="65535"
                    :required="form.delivery_type === 'smtp'"
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  />
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-slate-300 mb-1">Domínio (HELO, se necessário)</label>
                  <input
                    v-model="form.smtp_domain"
                    type="text"
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-1">Usuário SMTP</label>
                  <input
                    v-model="form.smtp_user_name"
                    type="text"
                    autocomplete="username"
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-1">Senha SMTP</label>
                  <div class="relative">
                    <input
                      v-model="form.smtp_password"
                      :type="showPassword ? 'text' : 'password'"
                      autocomplete="new-password"
                      class="w-full pl-3 pr-11 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                      :placeholder="editingId && form.smtp_password_configured ? 'Deixe em branco para manter' : ''"
                    />
                    <button
                      type="button"
                      class="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-400 hover:text-[#2AD7DE] focus:outline-none focus-visible:text-[#2AD7DE] rounded-r-lg"
                      :aria-label="showPassword ? 'Ocultar senha SMTP' : 'Mostrar senha SMTP'"
                      :aria-pressed="showPassword"
                      @click="showPassword = !showPassword"
                    >
                      <svg v-if="!showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    </button>
                  </div>
                  <label class="mt-2 flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <input v-model="form.clear_smtp_password" type="checkbox" class="rounded border-slate-500" />
                    Remover senha
                  </label>
                </div>

                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-1">Autenticação</label>
                  <select
                    v-model="form.smtp_authentication"
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 text-sm"
                  >
                    <option value="plain" class="bg-slate-900">Normal (PLAIN)</option>
                    <option value="login" class="bg-slate-900">LOGIN</option>
                    <option value="cram_md5" class="bg-slate-900">CRAM-MD5</option>
                  </select>
                </div>

                <div class="flex flex-col gap-3 justify-center">
                  <label class="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                    <input v-model="form.enable_starttls_auto" type="checkbox" class="rounded border-slate-500 w-4 h-4" />
                    STARTTLS automático
                  </label>
                  <label class="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                    <input v-model="form.enable_ssl_tls" type="checkbox" class="rounded border-slate-500 w-4 h-4" />
                    SSL / SMTPS (ex.: porta 465)
                  </label>
                </div>
              </div>
            </div>

            <div class="mt-6">
              <label class="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input v-model="form.active" type="checkbox" class="rounded border-slate-500 w-4 h-4" />
                Envio ativo
              </label>
            </div>
          </div>

          <div class="flex flex-wrap md:flex-nowrap items-center gap-3">
            <button
              v-if="form.delivery_type === 'smtp'"
              type="button"
              :disabled="testLoading || testSendLoading"
              class="px-4 py-2 rounded-lg border border-slate-500/80 text-slate-200 hover:bg-slate-800/60 transition disabled:opacity-50 text-sm font-medium"
              @click="runTest"
            >
              {{ testLoading ? 'Validando…' : 'Validar conexão' }}
            </button>
            <button
              v-if="form.delivery_type === 'smtp'"
              type="button"
              :disabled="testLoading || testSendLoading"
              class="px-4 py-2 rounded-lg border border-cyan-500/50 text-cyan-100 hover:bg-cyan-500/10 transition disabled:opacity-50 text-sm font-medium"
              @click="runTestSend"
            >
              {{ testSendLoading ? 'Enviando…' : 'Enviar e-mail de teste' }}
            </button>
            <button
              v-if="form.delivery_type === 'api'"
              type="button"
              :disabled="testLoading || testSendLoading"
              class="px-4 py-2 rounded-lg border border-slate-500/80 text-slate-200 hover:bg-slate-800/60 transition disabled:opacity-50 text-sm font-medium"
              @click="runApiTest"
            >
              {{ testLoading ? 'Testando…' : 'Validar conexão' }}
            </button>
            <button
              v-if="form.delivery_type === 'api'"
              type="button"
              :disabled="testLoading || testSendLoading"
              class="px-4 py-2 rounded-lg border border-cyan-500/50 text-cyan-100 hover:bg-cyan-500/10 transition disabled:opacity-50 text-sm font-medium"
              @click="runApiTestSend"
            >
              {{ testSendLoading ? 'Enviando…' : 'Enviar e-mail de teste' }}
            </button>
            <button
              v-if="hasConfig"
              type="button"
              class="px-4 py-2 rounded-lg border border-red-400/45 text-red-200 hover:bg-red-500/15 text-sm font-medium transition"
              @click="openDeleteConfirm"
            >
              Excluir configuração
            </button>
          </div>
          <div class="pt-4 border-t border-slate-700/70 flex items-center justify-end">
            <button
              type="submit"
              :disabled="saving"
              class="px-4 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold text-sm disabled:opacity-50 transition"
            >
              {{ saving ? 'Salvando…' : 'Salvar' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div
      v-if="saveConfirmOpen"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      @click.self="closeSaveConfirm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">Deseja salvar esta configuração de e-mail?</p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="saving"
            class="px-4 py-2 bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold rounded-lg transition disabled:opacity-50"
            @click="confirmSave"
          >
            {{ saving ? 'Salvando…' : 'Sim' }}
          </button>
          <button
            type="button"
            :disabled="saving"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition disabled:opacity-50"
            @click="closeSaveConfirm"
          >
            Não
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="deleteConfirm"
      class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl max-w-md w-full p-6 shadow-2xl">
        <h3 class="text-lg font-semibold text-slate-100 mb-2">Excluir configuração de e-mail?</h3>
        <p class="text-slate-300 mb-4">O ambiente ficará sem conta de envio até que seja configurada</p>
        <div class="flex justify-end gap-2">
          <button
            type="button"
            class="px-4 py-2 rounded-lg border border-slate-500 text-slate-200 hover:bg-slate-800/60"
            @click="deleteConfirm = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            :disabled="deleting"
            class="px-4 py-2 rounded-lg bg-red-500/20 border border-red-400/40 text-red-200 hover:bg-red-500/25 disabled:opacity-50"
            @click="executeDelete"
          >
            {{ deleting ? 'Excluindo…' : 'Excluir' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '../composables/useApi.js'

const DEFAULT_CONNECTOR_NAME = 'E-mail do ambiente'

export default {
  name: 'EmailDeliveryConnectors',

  data() {
    return {
      loading: false,
      loadError: '',
      connectors: [],

      editingId: null,
      showPassword: false,
      showApiKey: false,
      form: this.emptyForm(),

      formError: '',
      saving: false,
      testLoading: false,
      testSendLoading: false,
      testError: '',
      testResult: null,

      saveConfirmOpen: false,
      deleteConfirm: false,
      deleting: false
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    hasConfig() {
      return this.connectors.length > 0
    },
    singleConnector() {
      return this.connectors[0] || null
    },
    formDeliveryType() {
      return this.form.delivery_type
    }
  },

  watch: {
    formDeliveryType(newVal, oldVal) {
      if (oldVal !== undefined && newVal !== oldVal) {
        this.testError = ''
        this.testResult = null
        this.testLoading = false
        this.testSendLoading = false
      }
    }
  },

  methods: {
    openSaveConfirm() {
      if (this.saving) return
      this.saveConfirmOpen = true
    },

    closeSaveConfirm() {
      if (this.saving) return
      this.saveConfirmOpen = false
    },

    async confirmSave() {
      if (this.saving) return
      await this.save()
    },

    emptyForm() {
      return {
        delivery_type: 'smtp',
        from_address: '',
        reply_to: '',
        smtp_address: '',
        smtp_port: 587,
        smtp_domain: '',
        smtp_user_name: '',
        smtp_password: '',
        clear_smtp_password: false,
        smtp_password_configured: false,
        smtp_authentication: 'plain',
        enable_starttls_auto: true,
        enable_ssl_tls: false,
        active: true,
        api_provider: '',
        api_region: '',
        api_endpoint: '',
        api_key: '',
        clear_api_key: false,
        api_key_configured: false
      }
    },

    applyConnectorToForm(c) {
      const allowedProviders = ['sendgrid', 'mailgun', 'resend', 'postmark', 'amazon_ses', 'custom']
      const prov = c.api_provider && allowedProviders.includes(c.api_provider) ? c.api_provider : ''
      this.form = {
        delivery_type: c.delivery_type === 'api' ? 'api' : 'smtp',
        from_address: c.from_address || '',
        reply_to: c.reply_to || '',
        smtp_address: c.smtp_address || '',
        smtp_port: c.smtp_port ?? 587,
        smtp_domain: c.smtp_domain || '',
        smtp_user_name: c.smtp_user_name || '',
        smtp_password: '',
        clear_smtp_password: false,
        smtp_password_configured: !!c.smtp_password_configured,
        smtp_authentication: c.smtp_authentication || 'plain',
        enable_starttls_auto: c.enable_starttls_auto !== false,
        enable_ssl_tls: !!c.enable_ssl_tls,
        active: c.active !== false,
        api_provider: prov,
        api_region: c.api_region || '',
        api_endpoint: c.api_endpoint || '',
        api_key: '',
        clear_api_key: false,
        api_key_configured: !!c.api_key_configured
      }
    },

    async load() {
      const { get } = useApi()
      this.loading = true
      this.loadError = ''
      this.formError = ''
      const res = await get('/api/v1/email_delivery_connectors')
      this.loading = false

      if (res.error) {
        this.loadError = res.error.response?.data?.errors?.join?.(', ') || 'Erro ao carregar.'
        this.connectors = []
        this.editingId = null
        this.form = this.emptyForm()
        return
      }

      this.connectors = res.data?.email_delivery_connectors || []

      if (this.singleConnector) {
        this.editingId = this.singleConnector.id
        const detail = await get(`/api/v1/email_delivery_connectors/${this.editingId}`)
        if (detail.error || !detail.data?.email_delivery_connector) {
          this.formError = 'Não foi possível carregar os detalhes da configuração.'
          this.form = this.emptyForm()
          return
        }
        this.applyConnectorToForm(detail.data.email_delivery_connector)
      } else {
        this.editingId = null
        this.form = this.emptyForm()
      }

      this.showPassword = false
      this.showApiKey = false
    },

    async runTest() {
      if (this.form.delivery_type !== 'smtp') return

      this.testLoading = true
      this.testError = ''
      this.testResult = null
      const { post } = useApi()

      const payload = {
        email_delivery_connector: {
          id: this.editingId,
          delivery_type: 'smtp',
          smtp_address: this.form.smtp_address?.trim(),
          smtp_port: this.form.smtp_port,
          smtp_domain: this.form.smtp_domain?.trim() || null,
          smtp_user_name: this.form.smtp_user_name?.trim() || null,
          smtp_password: this.form.clear_smtp_password ? null : this.form.smtp_password,
          clear_smtp_password: this.form.clear_smtp_password,
          smtp_authentication: this.form.smtp_authentication,
          enable_starttls_auto: this.form.enable_starttls_auto,
          enable_ssl_tls: this.form.enable_ssl_tls
        }
      }

      const res = await post('/api/v1/email_delivery_connectors/test_connection', payload)
      this.testLoading = false

      if (res.error) {
        const d = res.error.response?.data
        this.testError = d?.error || d?.message || 'Falha ao validar SMTP.'
        return
      }
      this.testResult = res.data
    },

    async runTestSend() {
      if (this.form.delivery_type !== 'smtp') return

      this.testSendLoading = true
      this.testError = ''
      this.testResult = null
      const { post } = useApi()

      const payload = {
        email_delivery_connector: {
          id: this.editingId,
          delivery_type: 'smtp',
          from_address: this.form.from_address?.trim(),
          reply_to: this.form.reply_to?.trim() || null,
          smtp_address: this.form.smtp_address?.trim(),
          smtp_port: this.form.smtp_port,
          smtp_domain: this.form.smtp_domain?.trim() || null,
          smtp_user_name: this.form.smtp_user_name?.trim() || null,
          smtp_password: this.form.clear_smtp_password ? null : this.form.smtp_password,
          clear_smtp_password: this.form.clear_smtp_password,
          smtp_authentication: this.form.smtp_authentication,
          enable_starttls_auto: this.form.enable_starttls_auto,
          enable_ssl_tls: this.form.enable_ssl_tls
        }
      }

      const res = await post('/api/v1/email_delivery_connectors/test_send', payload)
      this.testSendLoading = false

      if (res.error) {
        const d = res.error.response?.data
        this.testError = d?.error || d?.message || 'Falha ao enviar e-mail de teste.'
        return
      }
      this.testResult = res.data
    },

    async runApiTestSend() {
      if (this.form.delivery_type !== 'api') return

      this.testSendLoading = true
      this.testError = ''
      this.testResult = null
      const { post } = useApi()

      const payload = {
        email_delivery_connector: {
          id: this.editingId,
          delivery_type: 'api',
          from_address: this.form.from_address?.trim(),
          reply_to: this.form.reply_to?.trim() || null,
          api_provider: this.form.api_provider,
          api_region: this.form.api_region?.trim() || null,
          api_endpoint: this.form.api_endpoint?.trim() || null,
          api_key: this.form.clear_api_key ? null : this.form.api_key,
          clear_api_key: this.form.clear_api_key
        }
      }

      const res = await post('/api/v1/email_delivery_connectors/test_send', payload)
      this.testSendLoading = false

      if (res.error) {
        const d = res.error.response?.data
        this.testError = d?.error || d?.message || 'Falha ao enviar e-mail de teste pela API.'
        return
      }
      this.testResult = res.data
    },

    async runApiTest() {
      if (this.form.delivery_type !== 'api') return

      this.testLoading = true
      this.testError = ''
      this.testResult = null
      const { post } = useApi()

      const payload = {
        email_delivery_connector: {
          id: this.editingId,
          delivery_type: 'api',
          api_provider: this.form.api_provider,
          api_region: this.form.api_region?.trim() || null,
          api_endpoint: this.form.api_endpoint?.trim() || null,
          api_key: this.form.clear_api_key ? null : this.form.api_key,
          clear_api_key: this.form.clear_api_key
        }
      }

      const res = await post('/api/v1/email_delivery_connectors/test_connection', payload)
      this.testLoading = false

      if (res.error) {
        const d = res.error.response?.data
        this.testError = d?.error || d?.message || 'Falha ao testar credenciais da API.'
        return
      }
      this.testResult = res.data
    },

    async save() {
      this.saving = true
      this.formError = ''
      const { post, put } = useApi()

      const body = {
        delivery_type: this.form.delivery_type,
        name: DEFAULT_CONNECTOR_NAME,
        from_address: this.form.from_address.trim(),
        reply_to: this.form.reply_to?.trim() || null,
        active: this.form.active
      }
      if (this.form.delivery_type === 'smtp') {
        body.smtp_address = this.form.smtp_address.trim()
        body.smtp_port = this.form.smtp_port
        body.smtp_domain = this.form.smtp_domain?.trim() || null
        body.smtp_user_name = this.form.smtp_user_name?.trim() || null
        body.smtp_authentication = this.form.smtp_authentication
        body.enable_starttls_auto = this.form.enable_starttls_auto
        body.enable_ssl_tls = this.form.enable_ssl_tls
        body.clear_smtp_password = this.form.clear_smtp_password
        body.smtp_password = this.form.clear_smtp_password ? null : this.form.smtp_password
      }
      if (this.form.delivery_type === 'api') {
        body.api_provider = this.form.api_provider
        body.api_region = this.form.api_region?.trim() || null
        body.api_endpoint = this.form.api_endpoint?.trim() || null
        body.clear_api_key = this.form.clear_api_key
        body.api_key = this.form.clear_api_key ? null : this.form.api_key
      }

      let res
      if (this.editingId) {
        res = await put(`/api/v1/email_delivery_connectors/${this.editingId}`, { email_delivery_connector: body })
      } else {
        res = await post('/api/v1/email_delivery_connectors', { email_delivery_connector: body })
      }

      this.saving = false

      if (res.error) {
        const msg = res.error.response?.data?.errors
        this.formError = Array.isArray(msg) ? msg.join(', ') : 'Erro ao salvar.'
        return
      }

      this.saveConfirmOpen = false
      this.testError = ''
      this.testResult = null
      await this.load()
    },

    openDeleteConfirm() {
      if (!this.hasConfig) return
      this.deleteConfirm = true
    },

    async executeDelete() {
      if (!this.editingId) return
      this.deleting = true
      const { delete: removeConnector } = useApi()
      const res = await removeConnector(`/api/v1/email_delivery_connectors/${this.editingId}`)
      this.deleting = false
      this.deleteConfirm = false
      if (res.error) {
        this.formError = 'Erro ao remover a configuração.'
        return
      }
      this.testError = ''
      this.testResult = null
      this.connectors = []
      this.editingId = null
      this.form = this.emptyForm()
      this.showPassword = false
      this.showApiKey = false
    }
  }
}
</script>
